import type { FixtureMeta } from '../../../../functions/tests/eval/recommendations/types'
import { ExplicitRating } from '../../../../functions/src/shared/types/gif'

/**
 * Round-trip helpers between the on-disk fixture `meta.json` shape
 * ({@link FixtureMeta}) and the flat form/bucket state the builder edits.
 *
 * Kept separate from the components so the builder and the triage inbox both
 * serialize back to disk identically — a fixture opened, verdict-triaged, and
 * saved must produce the same JSON shape the builder writes.
 *
 * New schema (2026-07-08): the load-bearing field is the prose `note` (fed to
 * the rubric judge verbatim). Only `traps` + `forbidden` gate; `good` is an
 * optional legacy diagnostic.
 */

export interface FixtureMetaForm {
  /** The fixture id/slug. Preserved verbatim when editing an existing one. */
  slug: string
  description: string
  conversationSnippet: string
  userIntent: string
  tags: string[]
  /** The viable-moves note — the judge scores boards against this. */
  note: string
}

export interface FixtureBuckets {
  /** Tempting-but-wrong ids. Hard gate. */
  traps: string[]
  /** Never-acceptable ids. Hard gate. */
  forbidden: string[]
  /** OPTIONAL legacy diagnostic ids. Never gates. */
  good: string[]
  /**
   * OPTIONAL curator gold tier: the 2–3 ids she'd be disappointed not to see.
   * A hard pool signal. Gold ids are always ALSO good (enforced on serialize);
   * a loaded gold id not in `good` is folded into `good` here so the invariant
   * holds in the builder's state.
   */
  gold: string[]
}

export const EMPTY_FORM: FixtureMetaForm = {
  slug: '',
  description: '',
  conversationSnippet: '',
  userIntent: '',
  tags: [],
  note: '',
}

const DEFAULT_EXPLICIT_RATING: ExplicitRating[] = [
  ExplicitRating.G,
  ExplicitRating.PG,
  ExplicitRating.PG_13,
]

/** Split a loaded fixture into the builder's form + bucket state. */
export function fixtureToForm(meta: FixtureMeta): {
  form: FixtureMetaForm
  buckets: FixtureBuckets
} {
  const ctx = meta.request?.context ?? {}
  return {
    form: {
      slug: meta.id ?? '',
      description: meta.description ?? '',
      conversationSnippet: ctx.conversationSnippet ?? '',
      userIntent: ctx.userIntent ?? '',
      tags: Array.isArray(meta.tags) ? [...meta.tags] : [],
      note: meta.expected?.note ?? '',
    },
    buckets: (() => {
      const gold = [...(meta.expected?.gold ?? [])]
      // Gold is implicitly good — fold any gold id not already in `good` into
      // the good set so the builder shows the invariant (gold ⊆ good) even for
      // an older fixture where the curator listed a gold id outside `good`.
      const good = [...new Set([...(meta.expected?.good ?? []), ...gold])]
      return {
        traps: [...(meta.expected?.traps ?? [])],
        forbidden: [...(meta.expected?.forbidden ?? [])],
        good,
        gold,
      }
    })(),
  }
}

/**
 * Serialize the builder state back to a {@link FixtureMeta}. Mirrors the field
 * ordering the on-disk fixtures use so diffs stay minimal. `originalRequest`
 * (from the loaded fixture) is spread first so request fields the builder
 * doesn't surface (maxResults, contentTypes, refinement, …) are preserved.
 */
export function formToFixture(
  form: FixtureMetaForm,
  buckets: FixtureBuckets,
  originalRequest?: FixtureMeta['request']
): FixtureMeta {
  const id = form.slug.trim() || 'NN-untitled-fixture'

  const context: Record<string, unknown> = { ...originalRequest?.context }
  if (form.conversationSnippet.trim()) {
    context.conversationSnippet = form.conversationSnippet
  } else {
    delete context.conversationSnippet
  }
  if (form.userIntent.trim()) {
    context.userIntent = form.userIntent
  } else {
    delete context.userIntent
  }

  const preferences: Record<string, unknown> = {
    ...originalRequest?.preferences,
  }
  preferences.explicitRating =
    originalRequest?.preferences?.explicitRating ?? DEFAULT_EXPLICIT_RATING
  // Response tone was removed end-to-end (2026-07-11). A legacy fixture's
  // stored `responseTone` rides in via the originalRequest spread above — strip
  // it so re-saving never re-serializes the retired field.
  delete preferences.responseTone

  const request = {
    ...originalRequest,
    context,
    preferences,
  } as FixtureMeta['request']

  // Gold is always ALSO good — union it into `good` so the on-disk fixture
  // keeps the invariant (gold ⊆ good) regardless of builder state ordering.
  const good = [...new Set([...buckets.good, ...buckets.gold])]

  const expected: FixtureMeta['expected'] = {
    note: form.note.trim(),
    traps: buckets.traps,
    forbidden: buckets.forbidden,
  }
  // `good` is optional — only emit it when the author kept some diagnostic ids.
  if (good.length > 0) {
    expected.good = good
  }
  // `gold` is optional — only emit the curator's hard tier when non-empty.
  if (buckets.gold.length > 0) {
    expected.gold = buckets.gold
  }

  return {
    id,
    description: form.description.trim() || '(no description)',
    tags: form.tags,
    request,
    expected,
  }
}

/** Stable stringify used both for the copy/download fallback and dirty checks. */
export function serializeFixture(meta: FixtureMeta): string {
  return JSON.stringify(meta, null, 2)
}
